'use client';
import { useEffect, useState } from 'react';

import { ScheduleContentTab } from './schedule-content-tab.component';
import { ScheduleHeaderModal } from './schedule-header-modal.component';
import { StandaloneAttentionContent } from './standalone-attention-content.component';
import { LaboratorialAttentionContent } from './laboratorial-attention-content.component';
import { HeaderTab } from './header-tab.component';

import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { SecondaryTabProvider, SecondaryTabs } from '@/components/ui/secondary-tab.component';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { TabsType, useScheduleTab } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-schedule-tab.hook';
import { ScheduleTabAnalytics } from '@/app/(main)/(pages)/busca/[slug]/_utils/schedule-tab-analytics.util';
import { partnerBranchDisplayName } from '@/utils/formatters/branch';
import { SearchType } from '@/types/most-search-type';

interface SearchScheduleModalProps {
  schedule?: SearchPartnerData;
  tabAnalytics: ScheduleTabAnalytics;
  procedureName: string;
  searchType: string;
  proceduresResearchedLength: number;
  open: boolean;
  onOpenChange(): void;
}

export function SearchScheduleModal({
  open,
  schedule,
  procedureName,
  searchType,
  proceduresResearchedLength,
  tabAnalytics,
  onOpenChange,
}: SearchScheduleModalProps) {
  const scheduleProceduresLength = schedule?.procedures.filter((procedure) => procedure.price !== undefined).length;
  const hasAllSelectedProcedures = scheduleProceduresLength === proceduresResearchedLength;
  const [standaloneTabAttention, setStandaloneTabAttention] = useState(false);
  const [laboratorialTabAttention, setLaboratorialTabAttention] = useState(false);
  const { tabs, selectedTab, onValueChange: onScheduleTabValueChange } = useScheduleTab(schedule, tabAnalytics);

  const hasProfessional = schedule?.professional?.id;
  const entityName = hasProfessional
    ? professionalDisplayName(schedule.professional)
    : partnerBranchDisplayName(schedule?.partner);
  const entityAvatar = hasProfessional ? schedule.professional.avatar : schedule?.partner.avatar;
  const entityAvatarType = hasProfessional ? EntityTypeEnum.PROFESSIONAL : EntityTypeEnum.LABORATORY_COLLECT_POINT;

  const tabsKey = tabs.map((tab) => tab.key).join('-');

  const onClose = (): void => {
    onScheduleTabValueChange(tabs[0].key);
    onOpenChange();
    setStandaloneTabAttention(false);
  };

  useEffect(() => {
    if ([SearchType.LABORATORIAL_EXAM, SearchType.VACCINE].includes(searchType as SearchType)) {
      setLaboratorialTabAttention(!hasAllSelectedProcedures);
    }
  }, [searchType, hasAllSelectedProcedures]);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="center-v2"
        className="md:max-w-[640px] lg:max-w-[800px] md:h-[800px] md:max-h-[800px] p-0"
        hasHeader
      >
        <div className="h-full p-5 flex flex-col">
          <ScheduleHeaderModal
            standaloneTabAttention={standaloneTabAttention}
            laboratorialTabAttention={laboratorialTabAttention}
            onOpenChange={onClose}
            onChangeContentToStandalone={setStandaloneTabAttention}
          />
          {standaloneTabAttention && (
            <StandaloneAttentionContent
              schedule={schedule as SearchPartnerData}
              onChangeContentToStandalone={setStandaloneTabAttention}
            />
          )}

          {laboratorialTabAttention && (
            <LaboratorialAttentionContent
              searchType={searchType}
              schedule={schedule as SearchPartnerData}
              onChangeToDefaultContent={setLaboratorialTabAttention}
              onClose={onClose}
            />
          )}

          {!standaloneTabAttention && !laboratorialTabAttention && (
            <>
              <div className="flex gap-4">
                <EntityAvatar
                  alt={`Foto de perfil de ${entityName}`}
                  size="lg"
                  src={entityAvatar}
                  type={entityAvatarType}
                />
                <span className="text-xl font-semibold line-clamp-2 h-fit" data-testid="schedule-modal--entity-name">
                  {entityName}
                </span>
              </div>

              <SecondaryTabProvider data-testid="schedule-modal--tab-scheduled">
                <SecondaryTabs
                  key={tabsKey}
                  defaultValue={tabs[0].key}
                  value={selectedTab}
                  className="w-full h-full mt-3 flex flex-col justify-start"
                  onValueChange={onScheduleTabValueChange}
                >
                  <HeaderTab tabs={tabs} />
                  <ScheduleContentTab
                    tabs={tabs}
                    schedule={schedule as SearchPartnerData}
                    procedureName={procedureName}
                    selectedTab={(selectedTab || tabs[0].key) as TabsType}
                    onChangeContentToStandalone={setStandaloneTabAttention}
                  />
                </SecondaryTabs>
              </SecondaryTabProvider>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
